/**
 * Aktivizimi i llogarisë nga linku i ftesës.
 *
 * Tri gjendje, secila me rrugën e vet përpara:
 *  · linku vlen — mirëseardhja me emrin dhe emailin, dhe kërkesat e
 *    fjalëkalimit që plotësohen ndërsa shkruhet;
 *  · linku s'vlen — më parë dilnin dy fusha bosh dhe një shirit i kuq
 *    «Kontakto administratorin»: personi s'kishte çfarë të bënte. Shkaku më i
 *    shpeshtë është një ftesë e dërguar sërish (emaili më i ri ka linkun e
 *    vlefshëm), ndaj kjo thuhet hapur, bashkë me rrugën «Harrove fjalëkalimin?»;
 *  · u aktivizua — konfirmim dhe buton drejt hyrjes.
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Stack, InputAdornment, Alert, CircularProgress, IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import apiService from '../../services/apiServices';
import helper from '../../services/helper';
import config from '../../config/config';
import NexumiLogo from '../common/NexumiLogo';

const API_URL = process.env.REACT_APP_API_URL || config.baseUrl.replace(/\/$/, '');

const BRAND = '#2F7D90';
const BRAND_DARK = '#1D5462';

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8, 'Fjalëkalimi duhet të ketë të paktën 8 karaktere')
    .required('Fjalëkalimi është i detyrueshëm'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Fjalëkalimet nuk përputhen')
    .required('Konfirmimi është i detyrueshëm'),
});

const goToLogin = () => { window.location.href = '/#/'; };

function Shell({ children }) {
  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #EEF7F9 0%, #F8FAFC 55%)', px: 2, py: { xs: 4, sm: 6 },
    }}>
      <Box sx={{ mb: 3 }}><NexumiLogo height={28} /></Box>
      <Box sx={{
        width: '100%', maxWidth: 440, bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E2E8F0',
        boxShadow: '0 20px 40px -24px rgba(15,23,42,0.25)', overflow: 'hidden',
      }}>
        <Box sx={{ height: 4, bgcolor: BRAND }} />
        <Box sx={{ p: { xs: 3, sm: 4 } }}>{children}</Box>
      </Box>
      <Typography sx={{ mt: 3, fontSize: 12.5, color: '#64748B', textAlign: 'center' }}>
        Keni nevojë për ndihmë? <Box component="a" href="mailto:support@nexuma.app" sx={{ color: BRAND, fontWeight: 600, textDecoration: 'none' }}>support@nexuma.app</Box>
      </Typography>
    </Box>
  );
}

function StateIcon({ children, tone = 'brand' }) {
  const tones = { brand: ['#EEF7F9', BRAND], error: ['#FEF2F2', '#DC2626'], success: ['#ECFDF5', '#059669'] };
  const [bg, fg] = tones[tone];
  return (
    <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
      {children}
    </Box>
  );
}

function Rule({ ok, children }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: ok ? '#059669' : '#64748B', transition: 'color .15s' }}>
      {ok ? <CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />}
      <Typography sx={{ fontSize: 13 }}>{children}</Typography>
    </Stack>
  );
}

const primaryBtn = {
  py: 1.4, fontSize: 15, fontWeight: 600, textTransform: 'none', borderRadius: '10px', boxShadow: 'none',
  bgcolor: BRAND, '&:hover': { bgcolor: BRAND_DARK, boxShadow: 'none' },
};

const SetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [success, setSuccess] = useState(false);
  const [invalid, setInvalid] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });
  const pw = watch('newPassword') || '';
  const pw2 = watch('confirmPassword') || '';

  useEffect(() => {
    const urlToken = helper().getParameterByName('token');
    if (!urlToken) {
      setInvalid('missing');
      setValidating(false);
      return;
    }
    fetch(`${API_URL}/api/auth/verify-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: urlToken, tenant: helper().getParameterByName('t') || undefined }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) {
          setToken(urlToken);
          setUserInfo(data.data);
        } else {
          setInvalid('expired');
        }
      })
      .catch(() => setInvalid('network'))
      .finally(() => setValidating(false));
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(goToLogin, 4000);
    return () => clearTimeout(t);
  }, [success]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService().post('auth/reset-password', { token, newPassword: data.newPassword, tenant: helper().getParameterByName('t') || undefined });
      if (response?.success) setSuccess(true);
      else if (/skaduar|pavlefsh/i.test(response?.error || '')) setInvalid('expired');
      else setError(response?.error || 'Vendosja e fjalëkalimit dështoi. Provoni sërish.');
    } catch (err) {
      setError(err?.message || 'Vendosja e fjalëkalimit dështoi. Provoni sërish.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Shell>
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress size={28} sx={{ color: BRAND }} />
          <Typography sx={{ fontSize: 14, color: '#64748B' }}>Duke kontrolluar linkun…</Typography>
        </Stack>
      </Shell>
    );
  }

  if (invalid) {
    return (
      <Shell>
        <StateIcon tone="error"><LinkOffRoundedIcon /></StateIcon>
        <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' }}>
          {invalid === 'network' ? 'Linku nuk u kontrollua dot' : 'Ky link nuk vlen më'}
        </Typography>
        {invalid === 'network' ? (
          <Typography sx={{ mt: 1, fontSize: 14.5, lineHeight: 1.6, color: '#334155' }}>
            Nuk u lidhëm dot me serverin. Kontrolloni internetin dhe rifreskoni faqen.
          </Typography>
        ) : (
          <>
            <Typography sx={{ mt: 1, fontSize: 14.5, lineHeight: 1.6, color: '#334155' }}>
              {invalid === 'missing'
                ? 'Linku është i paplotë — hapeni direkt nga butoni në email.'
                : 'Linku i ftesës ka skaduar ose është zëvendësuar nga një ftesë më e re.'}
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2.5, fontSize: 14, lineHeight: 1.7, color: '#475569' }}>
              <li>Nëse keni marrë më shumë se një email ftese, <b>përdorni më të fundit</b>.</li>
              <li>Nëse e keni vendosur fjalëkalimin më parë, thjesht hyni në llogari.</li>
              <li>Përndryshe, te hyrja zgjidhni <b>«Harrove fjalëkalimin?»</b> për një link të ri, ose kërkojeni nga administratori.</li>
            </Box>
          </>
        )}
        <Button fullWidth variant="contained" onClick={invalid === 'network' ? () => window.location.reload() : goToLogin} sx={{ ...primaryBtn, mt: 3 }}>
          {invalid === 'network' ? 'Rifresko faqen' : 'Shko te hyrja'}
        </Button>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <StateIcon tone="success"><TaskAltRoundedIcon /></StateIcon>
        <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' }}>
          Llogaria u aktivizua
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 14.5, lineHeight: 1.6, color: '#334155' }}>
          Tani mund të hyni me <b>{userInfo?.email}</b> dhe fjalëkalimin e ri. Po ju dërgojmë te hyrja…
        </Typography>
        <Button fullWidth variant="contained" onClick={goToLogin} sx={{ ...primaryBtn, mt: 3 }}>Hyr në llogari</Button>
      </Shell>
    );
  }

  const longEnough = pw.length >= 8;
  const matches = pw.length > 0 && pw === pw2;
  const passwordField = (name, label, shown, setShown, autoFocus) => (
    <TextField
      fullWidth
      autoFocus={autoFocus}
      label={label}
      type={shown ? 'text' : 'password'}
      autoComplete="new-password"
      {...register(name)}
      error={!!errors[name]}
      helperText={errors[name]?.message}
      InputProps={{
        startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setShown(!shown)} edge="end" size="small" aria-label={shown ? 'Fshih fjalëkalimin' : 'Shfaq fjalëkalimin'}>
              {shown ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
    />
  );

  return (
    <Shell>
      <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em' }}>
        Aktivizo llogarinë
      </Typography>
      <Typography sx={{ mt: 0.75, fontSize: 14.5, lineHeight: 1.6, color: '#334155' }}>
        {userInfo?.name ? <>Mirë se vini, <b>{userInfo.name}</b>. </> : null}
        Vendosni një fjalëkalim për të hyrë në Nexuma.
      </Typography>
      {userInfo?.email && (
        <Box sx={{ mt: 2, px: 1.5, py: 1, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Llogaria</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F172A', wordBreak: 'break-all' }}>{userInfo.email}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2} sx={{ mt: 3 }}>
          {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>}
          {/* Fusha e fshehur e emailit ndihmon menaxherët e fjalëkalimeve ta ruajnë me adresën e duhur. */}
          {userInfo?.email && <input type="email" name="username" autoComplete="username" value={userInfo.email} readOnly hidden />}
          {passwordField('newPassword', 'Fjalëkalimi i ri', showNew, setShowNew, true)}
          {passwordField('confirmPassword', 'Përsërite fjalëkalimin', showConfirm, setShowConfirm, false)}
          <Stack spacing={0.5} sx={{ px: 0.5 }}>
            <Rule ok={longEnough}>Të paktën 8 karaktere</Rule>
            <Rule ok={matches}>Të dy fjalëkalimet përputhen</Rule>
          </Stack>
          <Button type="submit" fullWidth variant="contained" disabled={loading || !token} sx={{ ...primaryBtn, mt: 1 }}>
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Aktivizo llogarinë'}
          </Button>
        </Stack>
      </form>
    </Shell>
  );
};

export default SetPassword;
